import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import Primary3DButton from "../../components/buttons/Primary3DButton"
import LinearProgress from "../../components/linearProgress/LinearProgress"
import OnboardingPageContentWrapper from "../../components/onboarding/onboardingPageContentWrapper/OnboardingPageContentWrapper"
import OnboardingPageWrapper from "../../components/onboarding/onboardingPageWrapper/OnboardingPageWrapper"
import { BACKGROUND_COLORS } from "../../constants"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"

/**
 * Pre-paywall credibility-proof screen — shown after the
 * "Joon is here for you!" step and before /paywall/trial, to surface
 * research backing (NYU + UC Berkeley partnerships) and the Bright
 * Futures 2024 study outcomes.
 *
 * Layout (top → bottom):
 *   headline  →  "Clinically validated in partnership with:"
 *   → NYU + Berkeley logos  →  3 stat cards  →  study source line
 *   → Continue CTA
 *
 * Mobile-first. All colors, spacing, and typography come from
 * @joonapp/web-shared tokens per joon-design-system.md.
 */

type Stat = {
  value: string
  label: string
}

// Stats pulled from the Joon Health Bright Futures Study (2024, n=90 parents
// of kids ages 6-12). Framings are chosen to (a) match the paper's actual
// measurements and (b) avoid "X% of families" misread on the percentage.
//   88% → DBD improvement rate over 8 weeks
//   73% → parents who reported reduced arguments (paper wording); framed
//         here around nagging/arguing since that's the pain point this
//         audience (ADHD moms of 5-12yo) feels daily
//   74% → parents who agreed Joon helped their child be more accountable
interface CredibilityProofScreenProps {
  onContinue: () => void
}

const CredibilityProofScreen = ({
  onContinue,
}: CredibilityProofScreenProps) => {
  const { t } = useTranslation()

  // Stat values are numeric and stay as-is; only the labels are translated.
  const STATS: Stat[] = [
    { value: "88%", label: t("PAYWALL_CREDIBILITY_STAT1_LABEL") },
    { value: "73%", label: t("PAYWALL_CREDIBILITY_STAT2_LABEL") },
    { value: "74%", label: t("PAYWALL_CREDIBILITY_STAT3_LABEL") },
  ]

  useEffect(() => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_PREPAYWALL_CREDIBILITY_PROOF)
  }, [])

  return (
    <OnboardingPageWrapper>
      <OnboardingPageContentWrapper
        paddingTop={SPACING.space6}
        backgroundOnDesktop={BACKGROUND_COLORS.xlightBlueBg}
      >
        <FlexBox
          direction="column"
          align="center"
          gap={SPACING.space4}
          wrap={false}
          fullWidth
        >
          <LinearProgress value={100} withIcon />
        </FlexBox>

        {/* All body content wrapped as a single top-level child of
            OnboardingPageContentWrapper. That wrapper uses
            justify="space-between", so with just 3 top-level children
            (progress / body / Continue) the extra vertical space on
            mobile gets distributed ABOVE and BELOW this block — not
            BETWEEN the groups A/B/C, which keeps their spacing
            consistent at every viewport height. Internal gap is
            SPACING.space5 (20px), slightly looser than the intra-group
            gaps (12px/8px) so the three groups read as distinct
            without feeling spread. */}
        <FlexBox
          direction="column"
          align="center"
          gap={SPACING.space5}
          wrap={false}
          fullWidth
        >
          {/* Group A — headline */}
          <Typography variant="h2Serif" textAlign="center">
            {t("PAYWALL_CREDIBILITY_HEADLINE")}
          </Typography>

          {/* Group B — "clinically validated" subhead + partnership
              logos. Tight space2 internal gap so the subhead's colon
              points straight at the logo row. */}
          <FlexBox
            direction="column"
            align="center"
            gap={SPACING.space2}
            wrap={false}
            fullWidth
          >
            <Typography
              variant="bodySmall"
              textAlign="center"
              color={JoonUIColor.text.secondary}
            >
              {t("PAYWALL_CREDIBILITY_VALIDATED")}
            </Typography>
            <FlexBox
              direction="row"
              align="center"
              justify="center"
              gap={SPACING.space4}
              wrap={false}
              fullWidth
            >
              {/* Both logos at equal 48px height; flexShrink:0 keeps the
                  row from compressing either on narrow viewports. */}
              <img
                src="/images/research-nyu.png"
                alt="New York University"
                style={{
                  height: "48px",
                  width: "auto",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
              <img
                src="/images/research-berkeley.png"
                alt="University of California, Berkeley"
                style={{
                  height: "48px",
                  width: "auto",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </FlexBox>
          </FlexBox>

          {/* Group C — stat cards + study-source line. Grouped at
              space3 (12px) so the source reads as a caption right
              under the data. */}
          <FlexBox
            direction="column"
            align="center"
            gap={SPACING.space3}
            wrap={false}
            fullWidth
          >
            <FlexBox
              direction="column"
              align="stretch"
              gap={SPACING.space3}
              wrap={false}
              fullWidth
              style={{ width: "min(100%, 360px)" }}
            >
              {STATS.map((stat) => (
                <StatCard key={stat.value} stat={stat} />
              ))}
            </FlexBox>
            <Typography
              variant="bodyXSmall"
              textAlign="center"
              color={JoonUIColor.text.secondary}
            >
              {t("PAYWALL_CREDIBILITY_STUDY_SOURCE")}
            </Typography>
          </FlexBox>
        </FlexBox>

        <FlexBox
          direction="column"
          align="center"
          gap={SPACING.space2}
          wrap={false}
          fullWidth
          style={{ position: "sticky", bottom: SPACING.space4 }}
        >
          <FlexBox
            direction="row"
            align="center"
            justify="center"
            gap={SPACING.space1}
            wrap={false}
          >
            <Typography variant="bodyBold" color={JoonUIColor.text.primary}>
              {t("PAYWALL_NO_PAYMENT_REQUIRED")}
            </Typography>
            <FontAwesomeIcon icon={faCheck} color={JoonUIColor.text.primary} />
          </FlexBox>
          <Primary3DButton
            text={t("ONBOARDING_CONTINUE")}
            onClick={onContinue}
          />
        </FlexBox>
      </OnboardingPageContentWrapper>
    </OnboardingPageWrapper>
  )
}

const StatCard = ({ stat }: { stat: Stat }) => (
  <FlexBox
    direction="row"
    align="center"
    gap={SPACING.space4}
    wrap={false}
    fullWidth
    style={{
      background: JoonUIColor.background.primaryNeutral,
      border: `1px solid ${JoonUIColor.border.default}`,
      borderRadius: SPACING.space2,
      padding: `${SPACING.space4} ${SPACING.space4}`,
      boxShadow: "2px 2px 2px #F0F3F8",
    }}
  >
    <div
      style={{
        minWidth: "88px",
        flexShrink: 0,
        textAlign: "center",
      }}
    >
      <Typography variant="h2" color={JoonColorExpanded.indigo500}>
        {stat.value}
      </Typography>
    </div>
    <Typography
      variant="bodySmall"
      textAlign="left"
      color={JoonUIColor.text.primary}
      style={{ flex: 1 }}
    >
      {stat.label}
    </Typography>
  </FlexBox>
)

export default CredibilityProofScreen
